/**
 * Next up (focusUI): on the open board the chores first, the hand-added tasks waiting in a pile
 * (owner's ruling 2026-09-29, docs/product.md); on the Done board the two most recent in full, the
 * rest in the same pile (2026-09-30). One rule for both clients: src/views/Board.tsx and
 * client/lib/board.ts layoutBoard both call partitionBoard, and the pile's geometry and the web's
 * pull past the end of the list live here too.
 *
 * Pure on purpose: Node tests import it (tests/next-up.test.ts). watchOverscroll touches only the
 * few members of a scroller and its events that it names, so it needs no DOM types.
 */

import { sinkOthers, type OthersOpts, type YoursFields } from "./yours.ts";

/** How many hand-added tasks the pile draws: the top card and two peeking out below it. */
export const PILE_SIZE = 3;
/** How far each card under the top one peeks out below it: a strip of its bronze frame. */
export const PILE_PEEK = 6;
/** How much darker each card under the top one is drawn, one step per card. */
export const PILE_SHADE = [0, 0.35, 0.55] as const;
/** The gap between cards in the list (.content gap; client/ui tokens space.gap). */
export const CARD_GAP = 12;
/** Cards added per frame when a list grows (client/lib/board.ts re-exports it). */
export const CARDS_PER_FRAME = 8;

// ---- the pull that fans the pile (owner's ruling 2026-09-29: resistance, not a timer) ----
//
// Everything below is measured in the finger's travel past the point where the list reached its
// end, whatever the platform reports (see watchOverscroll and nativePull for how each one gets
// it). The pile follows the finger from the first pixel, through a rubber band: the further the
// pull, the less each pixel adds (spreadFor). At COMMIT_PX it gives way and fans out on its own,
// as long as the pull has lasted MIN_PULL_MS; let go before that and it snaps back.

/**
 * The finger's travel past the end of the list that fans the pile out: about a fifth of a phone's
 * height, a deliberate pull. A scroll that overshoots the end, or a flick, travels much less past
 * the end before the finger lifts.
 */
export const COMMIT_PX = 140;
/**
 * A pull must have lasted this long when it reaches COMMIT_PX: a fast swipe that runs through the
 * end is over in less, a deliberate pull takes longer than this to travel COMMIT_PX anyway. If the
 * finger gets there sooner and stays, the pile gives way the moment this has passed.
 */
export const MIN_PULL_MS = 200;
/** How far towards its place in the list each card has spread when the pull commits (0 to 1). */
export const SPREAD_AT_COMMIT = 0.35;
/** The most a pull spreads the cards before it commits (a fast pull can go past COMMIT_PX). */
export const SPREAD_MAX = 0.45;
/** How long the cards take to run the rest of the way into the list once the pull commits. */
export const FAN_MS = 280;
/** How long the cards take to gather back into the stack, or to snap back when a pull is let go. */
export const GATHER_MS = 240;
/**
 * Once a pull is under way, the list may slip this far back from its end and the pull goes on: a
 * thumb pulling wobbles, and each wobble down scrolls the list back a pixel or two.
 */
export const HOLD_SLACK_PX = 32;
/**
 * Android only: a drag begun at the end of the list, held this long, fans the pile. Android reports
 * neither an offset past the end nor the finger once the list has taken the drag (nativePull), so
 * the system's own stretch is the resistance the finger feels and the time held stands in for the
 * distance.
 */
export const ANDROID_HOLD_MS = 450;
/** A pull that travelled less than this is not worth a telemetry event ("pile.pull"). */
export const PULL_REPORT_PX = 24;
/**
 * Nor one let go sooner than this: a flick that starts at the end reports a burst of travel and
 * its release in the same few milliseconds (seen on Android), noise rather than a pull.
 */
export const PULL_REPORT_MS = 100;

/**
 * How far the cards have spread (0 stacked, 1 in their places in the list) for a pull of `px`: a
 * rubber band, px / (px + COMMIT_PX), so the first pixels move the cards most and each one after
 * adds less, reaching SPREAD_AT_COMMIT at COMMIT_PX.
 */
export function spreadFor(px: number): number {
  if (!(px > 0)) return 0;
  return Math.min(SPREAD_MAX, (2 * SPREAD_AT_COMMIT * px) / (px + COMMIT_PX));
}

/**
 * iOS: the finger's travel for a bounce `bounce` px past the end of a scroller `viewH` tall.
 * UIScrollView's rubber band puts the content at (1 - 1 / (x * 0.55 / d + 1)) * d for a finger
 * `x` past the edge (d the scroller's height); this is its inverse, so an iOS pull is measured in
 * the same finger travel as everywhere else.
 */
export function fingerForBounce(bounce: number, viewH: number): number {
  if (!(bounce > 0) || !(viewH > 0)) return 0;
  const b = Math.min(bounce, viewH * 0.95);
  return (b / 0.55) * (viewH / (viewH - b));
}

/** What a pull came to, for the "pile.pull" telemetry event: the most it travelled, and for how long. */
export interface PullReport {
  travel: number;
  ms: number;
  committed: boolean;
}

/** Whether a pull is worth a telemetry event: one that committed, or travelled PULL_REPORT_PX over PULL_REPORT_MS. */
export function worthReporting(r: PullReport): boolean {
  return r.committed || (r.travel >= PULL_REPORT_PX && r.ms >= PULL_REPORT_MS);
}

export interface Timers {
  set(fn: () => void, ms: number): unknown;
  clear(id: unknown): void;
}
const realTimers: Timers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
};
const realNow = () => Date.now();

export interface PullJudgeHandlers {
  /** The pull moved: spread the cards to `spread` (spreadFor), following the finger. */
  onSpread(spread: number): void;
  /** Past the point of no return: fan the pile out. Nothing after this is reported. */
  onCommit(r: PullReport): void;
  /** Let go before the point of no return: snap back into the stack. */
  onSnapBack(r: PullReport): void;
}

export interface PullJudge {
  /** The finger is `px` past the end (0 or less: back at the end, the pull goes on). */
  pull(px: number): void;
  /**
   * Android: a drag begun at the end is held, with no distance to report; it commits after
   * ANDROID_HOLD_MS unless let go (the pile animates the spread over that time itself).
   */
  hold(): void;
  /** The finger lifted, the list scrolled back, the wheel stopped. */
  release(): void;
  /** The pile is going away: forget any pull without a word. */
  dispose(): void;
}

/**
 * The one judge of a pull for every client and platform: spreads the pile with the finger, fans it
 * out once the pull reaches COMMIT_PX and has lasted MIN_PULL_MS (or an Android hold has lasted
 * ANDROID_HOLD_MS), snaps it back when let go sooner. Pure: the clock and the timers are injected.
 */
export function judgePull(h: PullJudgeHandlers, timers: Timers = realTimers, now: () => number = realNow): PullJudge {
  let startedAt: number | null = null;
  let travel = 0;
  let last = 0;
  let committed = false;
  let disposed = false;
  let timer: unknown = null;
  const clear = () => {
    if (timer !== null) timers.clear(timer);
    timer = null;
  };
  const report = (done: boolean): PullReport => ({ travel: Math.round(travel), ms: startedAt === null ? 0 : Math.round(now() - startedAt), committed: done });
  const commit = () => {
    if (committed || disposed || startedAt === null) return;
    clear();
    committed = true;
    h.onCommit(report(true));
  };
  return {
    pull(px) {
      if (committed || disposed) return;
      if (startedAt === null) {
        if (!(px > 0)) return;
        startedAt = now();
      }
      last = Math.max(0, px);
      travel = Math.max(travel, last);
      h.onSpread(spreadFor(last));
      if (last < COMMIT_PX) return clear();
      const wait = MIN_PULL_MS - (now() - startedAt);
      if (wait <= 0) return commit();
      if (timer === null) timer = timers.set(() => ((timer = null), last >= COMMIT_PX && commit()), wait);
    },
    hold() {
      if (committed || disposed || startedAt !== null) return;
      startedAt = now();
      timer = timers.set(() => ((timer = null), commit()), ANDROID_HOLD_MS);
    },
    release() {
      clear();
      if (committed || disposed || startedAt === null) return;
      const r = report(false);
      startedAt = null;
      travel = 0;
      last = 0;
      h.onSnapBack(r);
    },
    dispose() {
      clear();
      disposed = true;
    },
  };
}

// ---- the pile stacks itself again when scrolled away from (owner's ruling 2026-09-30) ----

/**
 * Whether a fanned pile should gather back into its stack: its second card (the one that peeks
 * under the top card when stacked) lies wholly below what the reader can see, its top at or past
 * `viewBottom`, the bottom edge of the visible part of the scroller (the tab bar and the bottom
 * safe area count as not visible). Both are in the same coordinates, whichever the caller reads.
 * Scrolling down the list puts the second card above the view, never below it, so this never
 * fires on the way down. `secondTop` is null for a pile of fewer than two cards, or a second card
 * not yet placed: such a pile never restacks by scroll.
 */
export function shouldRestack({ secondTop, viewBottom }: { secondTop: number | null | undefined; viewBottom: number }): boolean {
  if (secondTop === null || secondTop === undefined || !Number.isFinite(secondTop) || !Number.isFinite(viewBottom)) return false;
  return secondTop >= viewBottom;
}

/**
 * How a fanned pile was gathered, for the "pile.gather" telemetry event: a tap on the tab already
 * open, which scrolls its list to the top (it took the back-to-top arrow's place, 2026-10-02), or
 * the list scrolled up until the second card is out of view.
 */
export type GatherBy = "tab" | "scroll";

/** Which board a pile is on: the open board's hand-added tasks, or the Done board's record. */
export type PileBoard = "open" | "done";

/** How many done tasks the Done board shows in full above its pile: the two most recent. */
export const DONE_IN_FULL = 2;

export interface PilePartition<T> {
  /** Drawn in full above the pile: every open chore on To do, the two most recent on Done. */
  full: T[];
  /** Everything after `full`, in board order: the pile's cards first, then the rest it hides. */
  waiting: T[];
  /** The first PILE_SIZE of `waiting`, drawn as a stack while the board is collapsed. */
  pile: T[];
  /** Whether the "Next up" heading shows: the open board with at least one open chore. */
  heading: boolean;
  /** Something above the pile and something in it: `waiting` waits in the pile until it fans out. */
  collapsed: boolean;
}

function pileUp<T>(full: T[], waiting: T[], heading: boolean): PilePartition<T> {
  const collapsed = full.length > 0 && waiting.length > 0;
  return { full, waiting, pile: collapsed ? waiting.slice(0, PILE_SIZE) : [], heading, collapsed };
}

/** A task the board pins above everything that is not: urgent (the red frame) or a luxury post. */
export function isPinned(t: { urgent?: number | boolean | null; luxury?: number | null }): boolean {
  return !!t.urgent || (typeof t.luxury === "number" && t.luxury > 0);
}

/**
 * The open board's order on this device: the board's own order outranks NEW (docs/focus.md, docs/
 * product.md "Urgent tasks first"), so urgent and luxury tasks (isPinned) stay on top in the
 * server's order, then the tasks new to this device (`pinned`), then the rest. Each group keeps
 * the server's relative order. Without an urgent or luxury task this is pinNew (src/seen.ts,
 * client/lib/seen.ts). Both clients call it before partitionBoard, which puts the chores back
 * above the luxury posts (the server's order since 2026-10-06); with Next up off the flat list
 * keeps this order, a luxury post above the chores while something is new.
 */
export function liftNew<T extends { id: string; urgent?: number | boolean | null; luxury?: number | null }>(tasks: ReadonlyArray<T>, pinned: ReadonlySet<string>): T[] {
  if (pinned.size === 0) return [...tasks];
  const top: T[] = [];
  const fresh: T[] = [];
  const rest: T[] = [];
  for (const t of tasks) (isPinned(t) ? top : pinned.has(t.id) ? fresh : rest).push(t);
  return [...top, ...fresh, ...rest];
}

/**
 * Splits the open board, already in its order, into Next up's parts. In full above the pile: every
 * chore, and every hand-added task that is urgent or a luxury post, kept in the board's own order
 * (the server sorts urgent first, then the chores, then the luxury posts, then the rest by waiting
 * time: "Urgent tasks first, then whatever has waited longest, with due chores in their place",
 * docs/product.md; chores above luxury, owner 2026-10-06), then the hand-added tasks new to
 * this device (owner, 2026-09-30: "New tasks should show above the stack but below chores"). The
 * rest of the hand-added tasks wait. `isNew` is the board's own judgement (the "New" pill:
 * src/seen.ts, client/lib/seen.ts); without it nothing is new. The heading shows over a chore or
 * an urgent or luxury task; new tasks alone still collapse the rest into the pile without one.
 * With nothing in full, nothing collapses and the whole list shows as it is (all of it in
 * `waiting`).
 *
 * The urgent tasks, the chores and the luxury posts are kept in that order here too, each group in
 * the board's order, because liftNew lifts every urgent or luxury task above the chores whenever
 * this device has a new task; without the regrouping a luxury post would climb back over the
 * chores on most visits (a new chore occurrence is a new task).
 *
 * In `waiting`, the tasks someone else is on or the wheel picked for someone else sink below the
 * rest (owner, 2026-10-12: "unassigned tasks should be above assigned tasks, tasks assigned to
 * others aren't as relevant to you"; shared/yours.ts sinkOthers, the same rule as Yours). Your own
 * stay with the unassigned. Nothing above the pile moves. Without `others` nothing sinks.
 */
export function partitionNextUp<T extends { schedule_id: string | null; urgent?: number | boolean | null; luxury?: number | null } & YoursFields>(
  ordered: ReadonlyArray<T>,
  isNew: (t: T) => boolean = () => false,
  others?: OthersOpts,
): PilePartition<T> {
  const urgent: T[] = [];
  const chores: T[] = [];
  const luxury: T[] = [];
  const fresh: T[] = [];
  const waiting: T[] = [];
  for (const t of ordered) (t.urgent ? urgent : t.schedule_id ? chores : isPinned(t) ? luxury : isNew(t) ? fresh : waiting).push(t);
  const first = [...urgent, ...chores, ...luxury];
  return pileUp([...first, ...fresh], sinkOthers(waiting, others), first.length > 0);
}

/**
 * Splits the Done board, newest first, into the same parts (owner's ruling 2026-09-30): the two
 * most recently finished in full, everything loaded after them waiting in the pile. No heading.
 * Two or fewer: nothing collapses.
 */
export function partitionDone<T>(ordered: ReadonlyArray<T>): PilePartition<T> {
  return pileUp(ordered.slice(0, DONE_IN_FULL), ordered.slice(DONE_IN_FULL), false);
}

/** Either board's partition, for the clients that render both from one component. */
export function partitionBoard<T extends { schedule_id: string | null; urgent?: number | boolean | null; luxury?: number | null } & YoursFields>(
  ordered: ReadonlyArray<T>,
  board: PileBoard,
  isNew?: (t: T) => boolean,
  others?: OthersOpts,
): PilePartition<T> {
  return board === "open" ? partitionNextUp(ordered, isNew, others) : partitionDone(ordered);
}

/**
 * The muted line under the pile: gone on both boards. The tab on the top card counts what the pile
 * hides ("14 more", pileMore), and a caption counting one more read as an off-by-one: the open
 * board's "15 tasks waiting" went first (owner, 2026-10-04, "Go ahead and fix it"), then the Done
 * board's "48 more done" over "47 more" the same day (owner: "Two issues here"). Kept as a function
 * so the boards' render paths stay as they are; it returns "" for every count and board.
 */
export function pileCaption(_count: number, _board: PileBoard = "open"): string {
  return "";
}

/**
 * The tab on a stacked pile's top card (2026-10-02 triage, item 6): the pull's visible door. It
 * counts the cards under the top one, every one the pile holds, not only the two drawn ("3 more",
 * "1 more"). A pile of one card has nothing under it: no tab.
 */
export function pileMore(count: number): string {
  if (!(count > 1)) return "";
  return `${count - 1} more`;
}

/** The tab's name for a screen reader: what it says and what it does. */
export function pileMoreLabel(count: number, board: PileBoard = "open"): string {
  const more = pileMore(count);
  if (!more) return "";
  return board === "done" ? `${more}, shows the older done tasks` : `${more}, shows the waiting tasks`;
}

export interface PileGeometry {
  /**
   * Each card's offset from the pile's top while stacked: the top card at 0, each one under it
   * placed so its bottom edge shows PILE_PEEK below the one above, whatever its height.
   */
  stacked: number[];
  /** Each card's offset from the pile's top in the ordinary list: the cards above it and the gaps. */
  fanned: number[];
  /** The pile's height while stacked: the top card and the strips peeking out below it. */
  height: number;
}

/** Where the pile's cards sit, stacked and fanned, from their heights (top card first). */
export function pileGeometry(heights: ReadonlyArray<number>): PileGeometry {
  const stacked: number[] = [];
  const fanned: number[] = [];
  const top = heights[0] ?? 0;
  let y = 0;
  let height = 0;
  heights.forEach((h, i) => {
    const s = i === 0 ? 0 : Math.round(top - h + PILE_PEEK * i);
    stacked.push(s);
    fanned.push(y);
    y += h + CARD_GAP;
    height = Math.max(height, s + h);
  });
  return { stacked, fanned, height: Math.round(height) };
}

// ---- the web's pull past the end (src/views/Board.tsx and client/screens/Board.tsx on the web) ----

/** The members of a scrolling element that watchOverscroll reads and listens on. */
export interface ScrollerLike {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  // `any`: an element's own listeners take its event map's types, which nothing narrower accepts.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addEventListener(type: string, listener: (e: any) => void, options?: { passive?: boolean }): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  removeEventListener(type: string, listener: (e: any) => void): void;
}
interface TouchLike {
  touches: ArrayLike<{ clientY: number }>;
}
interface WheelLike {
  deltaY: number;
  deltaMode: number;
}

/** How a web pull is made, for telemetry. */
export type PullVia = "touch" | "wheel";

export interface OverscrollHandlers {
  /** The list is pulled `px` past its end (the finger's travel, or the wheel's); called on every move. */
  onPull(px: number, via: PullVia): void;
  /** The pull ended: the finger lifted, the list scrolled back, or the wheel stopped. */
  onRelease(): void;
}

/**
 * A wheel that has not turned for this long has stopped: the pull it made is let go, and the next
 * turn starts a new one (which counts only if the list is at its end when it starts).
 */
export const WHEEL_IDLE_MS = 350;
/**
 * One notch of a mouse wheel counts as this much finger travel, so a deliberate four notches fan
 * the pile and one or two do not. A trackpad's (and a Mac mouse's) small pixel steps count as they
 * are: two fingers on a trackpad pull just as one on a phone does.
 */
export const WHEEL_NOTCH_PX = 40;

type Scroll = Pick<ScrollerLike, "scrollTop" | "scrollHeight" | "clientHeight">;

/** How far a scroller is past its end: Safari's rubber band scrolls past it; elsewhere 0 or less. */
export function pastEnd(el: Scroll): number {
  return el.scrollTop + el.clientHeight - el.scrollHeight;
}

/** Whether a scroller is at (or past) its end, within a pixel of rounding, or within `slack`. */
export function atEnd(el: Scroll, slack = 1): boolean {
  return pastEnd(el) >= -slack;
}

/** A wheel event's turn in finger pixels: a notch (a line or page step, or a big pixel step) is WHEEL_NOTCH_PX. */
export function wheelTravel(e: WheelLike): number {
  if (e.deltaMode !== 0 || Math.abs(e.deltaY) >= 50) return Math.sign(e.deltaY) * WHEEL_NOTCH_PX;
  return e.deltaY;
}

/**
 * Watches a scroller for a pull past its end and reports how far it has gone, in the finger's
 * travel: a finger dragging up while the list is at its end, measured from where the finger was
 * when the list got there (Chrome stops the list dead; Safari's rubber band also gives a scroll
 * position past the end, and the larger of the two counts), or a wheel turning down in a burst
 * that began with the list already at its end (a burst that scrolls the list to its end, trackpad
 * momentum included, never counts). A flick never counts: once the finger lifts nothing more is
 * measured. Every listener is passive: the scroll itself is never held up. Returns the unsubscribe.
 */
export function watchOverscroll(el: ScrollerLike, handlers: OverscrollHandlers, timers: Timers = realTimers): () => void {
  let anchor: number | null = null;
  let pulling = false;
  // The wheel: the travel of this burst, whether it began at the end, and its idle timer.
  let wheel = 0;
  let burst: "none" | "counts" | "ignored" = "none";
  let wheelTimer: unknown = null;
  // The list's height when the pull began: a pile spreading under the pull draws past the old end
  // (a transform adds to the scrollable area), and that must not count as leaving the end.
  let endHeight = 0;
  const end = (): Scroll => (pulling ? { scrollTop: el.scrollTop, clientHeight: el.clientHeight, scrollHeight: Math.min(el.scrollHeight, endHeight) } : el);

  const stopPull = () => {
    if (!pulling) return;
    pulling = false;
    handlers.onRelease();
  };
  const endWheel = () => {
    wheel = 0;
    burst = "none";
    if (wheelTimer !== null) timers.clear(wheelTimer);
    wheelTimer = null;
  };
  const pull = (px: number, via: PullVia) => {
    if (!pulling) {
      if (!(px > 0)) return;
      endHeight = el.scrollHeight;
      pulling = true;
    }
    handlers.onPull(px, via);
  };

  const onTouchStart = (e: TouchLike) => {
    endWheel();
    stopPull();
    anchor = e.touches.length === 1 && atEnd(el) ? e.touches[0]!.clientY : null;
  };
  const onTouchMove = (e: TouchLike) => {
    if (e.touches.length !== 1) {
      anchor = null;
      return stopPull();
    }
    const y = e.touches[0]!.clientY;
    if (!atEnd(end(), pulling ? HOLD_SLACK_PX : 1)) {
      anchor = null;
      return stopPull();
    }
    // Where the finger was when the list reached its end: the travel up from there is the pull.
    if (anchor === null || y > anchor) anchor = y;
    pull(Math.max(anchor - y, pastEnd(end())), "touch");
  };
  const onTouchEnd = () => {
    anchor = null;
    stopPull();
  };
  const onWheel = (e: WheelLike) => {
    if (burst === "none") burst = e.deltaY > 0 && atEnd(el) ? "counts" : "ignored";
    if (wheelTimer !== null) timers.clear(wheelTimer);
    wheelTimer = timers.set(() => {
      endWheel();
      stopPull();
    }, WHEEL_IDLE_MS);
    if (burst !== "counts") return;
    if (!atEnd(end(), pulling ? HOLD_SLACK_PX : 1)) {
      burst = "ignored";
      return stopPull();
    }
    wheel = Math.max(0, wheel + wheelTravel(e));
    pull(wheel, "wheel");
  };

  const opts = { passive: true };
  el.addEventListener("touchstart", onTouchStart, opts);
  el.addEventListener("touchmove", onTouchMove, opts);
  el.addEventListener("touchend", onTouchEnd, opts);
  el.addEventListener("touchcancel", onTouchEnd, opts);
  el.addEventListener("wheel", onWheel, opts);
  return () => {
    endWheel();
    anchor = null;
    pulling = false;
    el.removeEventListener("touchstart", onTouchStart);
    el.removeEventListener("touchmove", onTouchMove);
    el.removeEventListener("touchend", onTouchEnd);
    el.removeEventListener("touchcancel", onTouchEnd);
    el.removeEventListener("wheel", onWheel);
  };
}

// ---- native: the pull past the end from a ScrollView's own events (client/screens/Board.tsx) ----

/**
 * What a native board knows about its scroller, updated from the ScrollView's events: the offset,
 * the content and window heights, whether a finger is dragging it, and the finger's own travel up
 * since the list reached its end when touch moves arrive during the drag (a list too short to
 * scroll never takes the finger, so they always do there; iOS may deliver them during a scroll
 * too; Android takes the finger for the scroll and sends none).
 */
export interface NativeScrollState {
  y: number;
  contentH: number;
  viewH: number;
  dragging: boolean;
  touchPull: number;
  /** A pull is under way: the list may slip back HOLD_SLACK_PX from its end and it goes on. */
  holding?: boolean;
}

/** How far the native list is from its end: past it (iOS's bounce) is positive. */
export function nativePastEnd(s: Pick<NativeScrollState, "y" | "contentH" | "viewH">): number {
  return s.y - Math.max(0, s.contentH - s.viewH);
}

/** Whether a native list is at its end (or too short to scroll), within a pixel, or `slack` while a pull is under way. */
export function nativeAtEnd(s: NativeScrollState): boolean {
  if (s.contentH <= 0 || s.viewH <= 0) return false;
  return nativePastEnd(s) >= (s.holding ? -HOLD_SLACK_PX : -1);
}

/**
 * How far a native board is pulled past its end in finger travel, or null when it is not: the
 * finger's own travel when touch moves arrive, or iOS's bounce past the end turned back into the
 * finger travel that made it (fingerForBounce), whichever is larger. Only while a finger is on the
 * list: a flick's bounce, with the finger already lifted, is never a pull. Android's drag at the
 * end reports neither and is judged by time instead (PullJudge.hold, from client/screens/Board.tsx).
 */
export function nativePull(s: NativeScrollState): number | null {
  const short = s.contentH > 0 && s.viewH > 0 && s.contentH <= s.viewH + 1;
  const touch = s.touchPull > 0 && (short || nativeAtEnd(s)) ? s.touchPull : 0;
  const bounce = s.dragging && s.viewH > 0 ? fingerForBounce(nativePastEnd(s), s.viewH) : 0;
  const px = Math.max(touch, bounce);
  if (px > 0) return px;
  // Under way and back at the end (the finger eased off): the pull goes on at nothing.
  if (s.holding && (s.dragging || s.touchPull !== 0) && nativeAtEnd(s)) return 0;
  return null;
}
